import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Toast} from "primereact/toast";
import {Button} from "primereact/button";
import {classNames} from "primereact/utils";
import {MultiSelect} from "primereact/multiselect";
import {Dropdown} from "primereact/dropdown";
import {DataTable} from "primereact/datatable";
import {Column, ColumnFilterElementTemplateOptions} from "primereact/column";
import {Paginator} from "primereact/paginator";
import {Dialog} from "primereact/dialog";
import {Calendar} from "primereact/calendar";
import {InputText} from "primereact/inputtext";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks";
import {getAllSimpleUsers, getCampaignContactsWithPagination, getContactsByCampaignIdDashboard, clearContactsByCampaignIdDashboard, getLeadsOwnerStatistic, getUsers} from "../../../redux/api/api";
import {setCampaignContactsPageSize, clearCampaignContacts, setActiveClick} from "../../../redux/actions/actions";
import {activeDict} from "../../../dictionaries";
import CampaignLeadsListNav from "./CampaignLeadsListNav";
import StatusesByPeriodDetails from "../../Settings/StatusesByPeriodList/StatusesByPeriodDetails";

const COINCIDENCE_LABEL = 'Співпадіння';
const OWNER_LEADS_TAB = 'Ліди по Власнику';
const OWNER_ONLY_ORDER = new Set(['Total', 'Active', 'Inactive', 'AttemptsDone', 'Success', 'Unsuccess']);
const LIST_ONLY_ORDER = new Set([
    'ContactName', 'PhoneTypeId', 'CountCalled', 'IsDone', 'IsActive', 'CreateDateTime',
    'NewCall', 'LastCall', 'StatusName', 'TimeStart', 'TimeEnd', 'TimeInStatus',
    'AllCountContacts', 'IsActiveCount', 'InactiveCount', 'SuccessCount', 'UnsuccessCount',
    'UnsuccessActiveCount', 'UnsuccessInactiveCount', 'CoincidenceCount',
]);

const isOrderByAllowed = (tab: string, orderBy?: string) => {
    if (!orderBy) return true;
    if (tab === OWNER_LEADS_TAB) {
        if (LIST_ONLY_ORDER.has(orderBy) || orderBy.startsWith('PhoneType_')) return false;
        return true;
    }
    return !OWNER_ONLY_ORDER.has(orderBy);
};

const asStatArray = (value: any) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return Object.values(value);
    return [];
};

const pickStatValue = (source: any, keys: string[]) => {
    if (!source) return null;
    for (const key of keys) {
        const value = source[key];
        if (value !== undefined && value !== null && value !== '') {
            return value;
        }
    }
    return null;
};

const readOwnerMetric = (source: any, key: string) => {
    const value = source?.[key];
    if (value === undefined || value === null || value === '') return 0;
    return value;
};

const formatOwnerAttempts = (source: any) => `${readOwnerMetric(source, 'attemptsDone')}/${readOwnerMetric(source, 'attemptsPlanned')}`;

const OwnerTypesDashboard = ({types}: {types: any[]}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [boxes, setBoxes] = useState<Array<{left: string; width: string}>>([]);
    const [pads, setPads] = useState({name: '', metric: ''});

    useLayoutEffect(() => {
        const root = rootRef.current;
        const host = root?.parentElement;
        const ownerRow = root?.closest('tr')?.previousElementSibling as HTMLTableRowElement | null;
        if (!host || !ownerRow) return;

        const measure = () => {
            const cells = Array.from(ownerRow.children) as HTMLElement[];
            const hostRect = host.getBoundingClientRect();
            if (!cells.length || !hostRect.width) return;
            const edges = cells.map((cell) => ((cell.getBoundingClientRect().left - hostRect.left) / hostRect.width) * 100);
            edges.push(100);
            const nextBoxes = edges.slice(0, -1).map((left, index) => ({
                left: `${left}%`,
                width: `${Math.max(0, edges[index + 1] - left)}%`,
            }));
            const nameStyle = getComputedStyle(cells[1] || cells[0]);
            const metricStyle = getComputedStyle(cells[2] || cells[0]);
            const nextPads = {name: nameStyle.padding, metric: metricStyle.padding};
            const signature = nextBoxes.map((box) => `${box.left}:${box.width}`).join('|');
            setBoxes((prev) => (prev.map((box) => `${box.left}:${box.width}`).join('|') === signature ? prev : nextBoxes));
            setPads((prev) => (prev.name === nextPads.name && prev.metric === nextPads.metric ? prev : nextPads));
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(ownerRow);
        observer.observe(host);
        return () => observer.disconnect();
    }, [types]);

    return (
        <div className="campaign-leads-owner-expansion" ref={rootRef}>
            {types.map((type: any) => {
                const values = [
                    null,
                    type?.phoneTypeName,
                    readOwnerMetric(type, 'total'),
                    readOwnerMetric(type, 'active'),
                    readOwnerMetric(type, 'inactive'),
                    formatOwnerAttempts(type),
                    readOwnerMetric(type, 'success'),
                    readOwnerMetric(type, 'unsuccess'),
                ];
                return (
                    <div key={type?.phoneTypeName} className="campaign-leads-owner-expansion__row">
                        {values.map((value, index) => {
                            const box = boxes[index];
                            if (!box) return null;
                            if (index === 0) {
                                return <div key={index} className="campaign-leads-owner-expansion__cell" style={box}/>;
                            }
                            if (index === 1) {
                                return (
                                    <div key={index} className="campaign-leads-owner-expansion__cell" style={{...box, padding: pads.name}}>
                                        <span className="campaign-leads-owner-expansion__name">{value}</span>
                                    </div>
                                );
                            }
                            return (
                                <div key={index} className="campaign-leads-owner-expansion__cell" style={{...box, padding: pads.metric}}>
                                    <div className="pl-2">{value}</div>
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

const CampaignLeadsList = (props: any) => {
    const dispatch = useAppDispatch();
    const paginationPage = useAppSelector(state => state.StatusesByPeriodListReducer.paginationPage);
    const paginationSize = useAppSelector(state => state.StatusesByPeriodListReducer.paginationSize);
    const [visible, setVisible] = useState<boolean>(false);
    const toast = useRef<Toast>(null);
    const [first, setFirst] = useState(0);
    const [hasStartFilter, setHasStartFilter] = useState<boolean>(false);
    const [hasEndFilter, setHasEndFilter] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState<number>(paginationSize);
    const hasInitializedPagination = useRef(false);
    const tabSwitchInProgress = useRef(false);
    const paginationChangeInProgress = useRef(false);
    const prevLeadsDashboardKeyRef = useRef('');
    const [orderBy, setOrderBy] = useState<any>(null);
    const [isDescFilter, setIsDescFilter] = useState<boolean>(true);
    const [filterStatusesId, setFilterStatusesId] = useState<any>(null);
    const [filterTypeId, setFilterTypeId] = useState<any>(null);
    const [filterStatusName, setFilterStatusName] = useState<any>(null);
    const [filterStatusId, setFilterStatusId] = useState<any>(null);
    const [filterStatusIds, setFilterStatusIds] = useState<any>(null);
    const [filterChangeType, setFilterChangeType] = useState<any>(null);
    const [filterContactName, setFilterContactName] = useState<any>(null);
    const [filterPhoneNo, setFilterPhoneNo] = useState<any>(null);
    const [filterDescription, setFilterDescription] = useState<any>(null);
    const [filterIsActive, setFilterIsActive] = useState<any>(null);
    const [filterPhoneTypeId, setFilterPhoneTypeId] = useState<any>(null);
    const [filterIsDone, setFilterIsDone] = useState<any>(null);
    const [filterCallOwner, setFilterCallOwner] = useState<any>(null);
    const [expandedRows, setExpandedRows] = useState<any>(null);
    const [sortOrder, setSortOrder] = useState<number>(0);
    const [page, setPage] = useState<any>(1);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
    const [selectedRowExpansion, setSelectedRowExpansion] = useState<any | null>(null);
    const [duration] = useState<boolean>(false);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const hasUsersAccess = !!rolesVision?.hasOwnProperty('Users');
    const [durationStartTime, setdurationStartTime] = useState<any>(getDefaultStartTime());
    const [durationEndTime, setdurationEndTime] = useState<any>(getDefaultStartTime());
    const [timeInStatusStartTime, setTimeInStatusStartTime] = useState<any>(getDefaultStartTime());
    const [timeInStatusEndTime, setTimeInStatusEndTime] = useState<any>(getDefaultStartTime());
    const [hasTimeInStatusFilter, setHasTimeInStatusFilter] = useState<boolean>(false);
    const justAppliedTimeInStatusFilter = useRef<boolean>(false);
    const timeInStatusFilterRequestSent = useRef<boolean>(false);
    const [filterDateTime, setFilterDateTime] = useState<any>(false);
    const [filterEndDateTime, setFilterEndDateTime] = useState<any>(false);
    const [filterFinishDateTime, setFilterFinishDateTime] = useState<any>(false);
    const [filterFinishEndDateTime, setFilterFinishEndDateTime] = useState<any>(false);
    const [startTime, setStartTime] = useState<any>(null);
    const [endTime, setEndTime] = useState<any>(null);
    const [startFinishTime, setStartFinishTime] = useState<any>(null);
    const [endFinishTime, setEndFinishTime] = useState<any>(null);
    const [isStartDateFocused, setIsStartDateFocused] = useState<boolean>(false);
    const [isEndDateFocused, setIsEndDateFocused] = useState<boolean>(false);
    const [isStartFinishTimeFocused, setIsStartFinishTimeFocused] = useState<boolean>(false);
    const [isEndFinishTimeFocused, setIsEndFinishTimeFocused] = useState<boolean>(false);
    const [allTimeChecked, setAllTimeChecked] = useState<boolean | null>(false);
    const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
    const selectedCampaignRef = useRef<any>(null);
    const [filterLastCallDateTime, setFilterLastCallDateTime] = useState<any>(false);
    const [filterLastCallEndDateTime, setFilterLastCallEndDateTime] = useState<any>(false);
    const [startLastCallTime, setStartLastCallTime] = useState<any>(null);
    const [endLastCallTime, setEndLastCallTime] = useState<any>(null);
    const [filterNewCallDateTime, setFilterNewCallDateTime] = useState<any>(false);
    const [filterNewCallEndDateTime, setFilterNewCallEndDateTime] = useState<any>(false);
    const [startNewCallTime, setStartNewCallTime] = useState<any>(null);
    const [endNewCallTime, setEndNewCallTime] = useState<any>(null);
    const contactsLoader = useAppSelector(state => state.CompaniesListReducer.contactsLoader)
    const clearLeadsFilters = useAppSelector(state => state.CompaniesListReducer.clearLeadsFilters)
    const campaignDirectoriesPhoneType = useAppSelector(state => state.CompaniesListReducer.campaignDirectoriesPhoneType)
    const usersData = useAppSelector(state => state.CompaniesListReducer.allSimpleUsers)
    const [value, setValue] = useState('Список лідів');
    const campaignContactsPageSize = useAppSelector(state => state.CompaniesListReducer.campaignContactsPageSize);
    const campaignContactsPagination = useAppSelector(state => state.CompaniesListReducer.campaignContactsPagination);
    const campaignContacts = useAppSelector(state => state.CompaniesListReducer.campaignContacts)
    const dataWithKeys = useMemo(() => {
        return (campaignContacts?.items || []).map((item: any, idx: number) => ({
            ...item,
            __rowKey: item?.types && item?.total
                ? `owner-${item?.ownerUserName || item?.main?.ownerUserName || idx}-${idx}`
                : `${item?.main?.contactName || ''}-${idx}`
        }));
    }, [campaignContacts?.items]);
    const leadPhoneTypeColumns = useMemo(() => {
        const dictionaryTypes = (campaignDirectoriesPhoneType || [])
            .filter((item: any) => item?.isActive !== false)
            .map((item: any) => ({
                name: item?.name,
                phoneTypeId: item?.dictionaryId,
                sortField: `PhoneType_${item?.dictionaryId}`,
                isCoincidence: false,
            }))
            .filter((item: any) => {
                const name = (item.name || '').toString().trim();
                return name && name.toLowerCase() !== COINCIDENCE_LABEL.toLowerCase();
            });

        const firstItem = campaignContacts?.items?.[0];
        const inferredTypes = asStatArray(firstItem?.phoneTypes || firstItem?.types || firstItem?.phoneTypeStatistic)
            .map((item: any) => ({
                name: item?.name || item?.phoneTypeName || item?.typeName,
                phoneTypeId: item?.phoneTypeId ?? item?.typeId ?? item?.dictionaryId ?? item?.id,
                sortField: `PhoneType_${item?.phoneTypeId ?? item?.typeId ?? item?.dictionaryId ?? item?.id ?? item?.name}`,
                isCoincidence: false,
            }))
            .filter((item: any) => {
                const name = (item.name || '').toString().trim();
                return name && name.toLowerCase() !== COINCIDENCE_LABEL.toLowerCase();
            });

        const typeColumns = dictionaryTypes.length ? dictionaryTypes : inferredTypes;

        return [
            ...typeColumns,
            {
                name: COINCIDENCE_LABEL,
                phoneTypeId: null,
                sortField: 'CoincidenceCount',
                isCoincidence: true,
            },
        ];
    }, [campaignDirectoriesPhoneType, campaignContacts?.items]);
    const [sortData, setSortData] = useState<any>(() => {
        const defaultStartDate = new Date();
        defaultStartDate.setHours(0, 0, 0, 0);
        const defaultEndDate = new Date();
        defaultEndDate.setHours(23, 59, 59, 0);

        const formatDateForFilter = (date: Date) => {
            const month = (date.getMonth() + 1).toString().padStart(2, "0");
            const day = date.getDate().toString().padStart(2, "0");
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
        };

        const formatTimeForFilter = (date: Date) => {
            const hours = date.getHours().toString().padStart(2, "0");
            const minutes = date.getMinutes().toString().padStart(2, "0");
            const seconds = date.getSeconds().toString().padStart(2, "0");
            return `${hours}:${minutes}:${seconds}`;
        };

        const startDate = formatDateForFilter(defaultStartDate);
        const endDate = formatDateForFilter(defaultEndDate);
        const startTimeStr = formatTimeForFilter(defaultStartDate);
        const endTimeStr = formatTimeForFilter(defaultEndDate);

        const defaultData: any = {
            pageNumber: page ? page : paginationPage,
            pageSize: paginationSize,
            campaignId: selectedCampaign,
            sort: {
                orderBy: orderBy ?? (value === 'Ліди по Власнику' ? 'OwnerUserName' : 'ContactName'),
                isDesc: isDescFilter
            },
            filter: [{
                "key": "CreateDateTime",
                "value": `${startDate} ${startTimeStr},${endDate} ${endTimeStr}`
            }]
        };

        if (selectedCampaign) {
            const campaignIdValue = typeof selectedCampaign === 'string' || typeof selectedCampaign === 'number'
                ? Number(selectedCampaign)
                : Number(selectedCampaign?.id || selectedCampaign);
            if (campaignIdValue) {
                defaultData.campaignId = campaignIdValue;
            }
        }

        return defaultData;
    });

    function getDefaultStartTime(): Date {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }

    const activeClick = useAppSelector(state => state.UsersListReducer.activeClick);

    useEffect(() => {
        const hash = window.location.hash || '';
        if (hash.includes('/leads') && hash.includes('campaignId=')) {
            const queryPart = hash.includes('?') ? hash.split('?')[1] : '';
            const campaignIdParam = new URLSearchParams(queryPart).get('campaignId');
            if (campaignIdParam) {
                const campaignId = Number(campaignIdParam);
                if (!isNaN(campaignId)) {
                    dispatch(setActiveClick({
                        item: '/leads',
                        from: hash || '#/leads',
                        click: true,
                        campaignId
                    }));
                }
            }
        }
    }, [dispatch]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (campaignContactsPagination.hasNext && campaignContactsPagination.pageNumber) {
            setTotalItems((campaignContactsPagination.pageNumber * campaignContactsPageSize.size) + campaignContactsPageSize.size)
        } else if (!campaignContactsPagination.hasNext && campaignContactsPagination.pageNumber) {
            setTotalItems(campaignContactsPagination.pageNumber * campaignContactsPageSize.size);
        }
    }, [campaignContactsPagination, campaignContactsPageSize]);


    useEffect(() => {
        if (selectedCampaign) {
            dispatch(setCampaignContactsPageSize({
                page: 1,
                size: 10
            }));
            setFirst(0);
        }
    }, [selectedCampaign, dispatch]);

    useEffect(() => {
        if (hasInitializedPagination.current && selectedCampaign) {
            const campaignIdValue = typeof selectedCampaign === 'string' || typeof selectedCampaign === 'number'
                ? Number(selectedCampaign)
                : Number(selectedCampaign?.id || selectedCampaign);

            if (campaignIdValue) {
                setSortData((prevSortData: any) => {
                    const updatedSortData = {
                        ...prevSortData,
                        campaignId: campaignIdValue,
                        pageNumber: 1
                    };
                    return updatedSortData;
                });
            }
        } else if (hasInitializedPagination.current && !selectedCampaign) {
            setSortData((prevSortData: any) => {
                const updatedSortData = { ...prevSortData };
                delete updatedSortData.campaignId;
                return updatedSortData;
            });
        }
    }, [selectedCampaign]);

    useEffect(() => {
        setExpandedRows(null);
    }, [dataWithKeys]);

    useEffect(() => {
        if (hasUsersAccess) {
            dispatch(getAllSimpleUsers(jwtToken?.jwtToken));
        }
    }, [hasUsersAccess, jwtToken?.jwtToken])

    useEffect(() => {
        const newFirst = (campaignContactsPageSize.page - 1) * campaignContactsPageSize.size;
        setFirst(newFirst);
    }, [campaignContactsPageSize.page, campaignContactsPageSize.size]);

    useEffect(() => {
        setStartTime(new Date("2024-10-29 00:00:00"));
        setEndTime(() => {
            const d = new Date();
            d.setHours(23, 59, 59, 0);
            return d;
        });
        setFilterDateTime(new Date());
        setFilterEndDateTime(new Date());
    }, []);

    useEffect(() => {
        if (allTimeChecked !== null && allTimeChecked) {
            setStartTime(new Date("2023-06-01T00:00:00"));
            setEndTime(() => {
                const date = new Date();
                date.setHours(23, 59, 59, 0);
                return date;
            });
            setFilterDateTime(new Date("2023-06-01T00:00:00"));
            setFilterEndDateTime(new Date());
        } else if (allTimeChecked !== null && !allTimeChecked) {
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            setStartTime(startOfToday);
            setEndTime(() => {
                const date = new Date();
                date.setHours(23, 59, 59, 0);
                return date;
            });
            setFilterDateTime(new Date());
            setFilterEndDateTime(new Date());
        }
    }, [allTimeChecked]);

    function getDefaultStartDate(): Date {
        return new Date();
    }

    function getDefaultEndDate(): Date {
        return new Date();
    }

    function getDefaultEndTime(): Date {
        const d = new Date();
        d.setHours(23, 59, 59, 0);
        return d;
    }

    const formatDateTime = (dateString: any) => {
        const date = new Date(dateString);
        return `${date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', second: '2-digit'})} ${date.toLocaleDateString('en-GB')}`;
    };

    const formatDate = (dateString: Date) => {
        const dateObj = new Date(dateString);
        const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
        const day = dateObj.getDate().toString().padStart(2, "0");
        const year = dateObj.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const getTime = (dateString: Date | any, isEnd?: boolean) => {
        const dateObj = new Date(dateString);
        const hours = dateObj.getHours().toString().padStart(2, "0");
        const minutes = dateObj.getMinutes().toString().padStart(2, "0");
        const seconds = dateObj.getSeconds().toString().padStart(2, "0");
        return `${hours}:${minutes}:${seconds}`;
    };

    const resetTableHorizontalScroll = () => {
        const scrollableBody = document.querySelector('.p-datatable-wrapper');
        if (scrollableBody) {
            scrollableBody.scrollTop = 0;
            scrollableBody.scrollLeft = 0;
        }
    };

    const defaultOrderBy = value === 'Ліди по Власнику' ? 'OwnerUserName' : 'ContactName';

    const fetchCampaignLeads = (data: any, options?: {forceDashboard?: boolean}) => {
        if (!isOrderByAllowed(value, data?.sort?.orderBy)) return;
        if (value === 'Ліди по Власнику') {
            dispatch(getLeadsOwnerStatistic(data, jwtToken?.jwtToken));
        } else {
            dispatch(getCampaignContactsWithPagination(data, jwtToken?.jwtToken));
        }

        const campaignId = Number(data?.campaignId);
        if (!campaignId || !jwtToken?.jwtToken) return;
        const dashboardPayload = {
            campaignId,
            filter: data?.filter || []
        };
        const dashboardKey = JSON.stringify(dashboardPayload);
        if (!options?.forceDashboard && prevLeadsDashboardKeyRef.current === dashboardKey) return;
        prevLeadsDashboardKeyRef.current = dashboardKey;
        dispatch(getContactsByCampaignIdDashboard(dashboardPayload, jwtToken.jwtToken));
    };

    useEffect(() => {
        if (hasInitializedPagination.current) {
            setFirst(0);
            setPage(1);
        }
    }, [filterStatusesId, filterStatusName, filterStatusId, filterTypeId, filterChangeType, filterStatusIds, selectedCampaign]);

    useEffect(() => {
        if (!hasInitializedPagination.current) {
            hasInitializedPagination.current = true;
            dispatch(setCampaignContactsPageSize({
                page: 1,
                size: 10
            }))
            return;
        }
    }, [dispatch]);

    useEffect(() => {
        if (!filterPhoneNo?.value) setPhoneNoFilterInputValue(undefined);
    }, [filterPhoneNo?.value]);

    useEffect(() => {
        if (hasStartFilter && !!selectedCampaign) {
            setFirst(0);
            dispatch(setCampaignContactsPageSize({
                page: 1,
                size: campaignContactsPageSize.size
            }));
            let filter: { key: string; value: string }[] = [];
            const campaignIdValue = selectedCampaign ? (typeof selectedCampaign === 'string' || typeof selectedCampaign === 'number' ? Number(selectedCampaign) : Number(selectedCampaign?.id || selectedCampaign)) : null;

            const dataSort: any = {
                pageNumber: 1,
                pageSize: campaignContactsPageSize.size,
                sort: {
                    orderBy: orderBy ?? defaultOrderBy,
                    isDesc: isDescFilter
                }
            }

            if (campaignIdValue) {
                dataSort.campaignId = campaignIdValue;
            }

            if (filterContactName?.value) {
                filter.push(filterContactName);
            }
            if (filterPhoneTypeId) {
                filterPhoneTypeId.map((item: any) => {
                    filter.push(item);
                })
            }
            if (filterIsDone) {
                filterIsDone.map((item: any) => {
                    filter.push(item);
                })
            }
            if (filterDateTime && filterEndDateTime && !allTimeChecked) {
                let startDate = formatDate(filterDateTime);
                let endDate = formatDate(filterEndDateTime);

                if (startTime && endTime) {
                    let filterStartTime = getTime(startTime);
                    let filterEndTime = getTime(endTime);
                    filter.push({
                        "key": "CreateDateTime",
                        "value": `${startDate} ${filterStartTime},${endDate} ${filterEndTime}`
                    })
                }
            }
            if (filterLastCallDateTime && filterLastCallEndDateTime) {
                let startDate = formatDate(filterLastCallDateTime);
                let endDate = formatDate(filterLastCallEndDateTime);

                if (startLastCallTime && endLastCallTime) {
                    let filterStartTime = getTime(startLastCallTime);
                    let filterEndTime = getTime(endLastCallTime);
                    filter.push({
                        "key": "LastCall",
                        "value": `${startDate} ${filterStartTime},${endDate} ${filterEndTime}`
                    })
                }
            }
            if (filterNewCallDateTime && filterNewCallEndDateTime) {
                let startDate = formatDate(filterNewCallDateTime);
                let endDate = formatDate(filterNewCallEndDateTime);

                if (startNewCallTime && endNewCallTime) {
                    let filterStartTime = getTime(startNewCallTime);
                    let filterEndTime = getTime(endNewCallTime);
                    filter.push({
                        "key": "NewCall",
                        "value": `${startDate} ${filterStartTime},${endDate} ${filterEndTime}`
                    })
                }
            }
            if (filterPhoneNo?.value) {
                filter.push(filterPhoneNo);
            }
            if (filterIsActive?.value) {
                filter.push(filterIsActive);
            }
            if (filterDescription?.value) {
                filter.push(filterDescription);
            }

            if (filterCallOwner) {
                filterCallOwner?.map((item: any) => {
                    filter.push(item);
                })
            }

            if (filter.length > 0) {
                dataSort['filter'] = filter;
            }

            fetchCampaignLeads(dataSort, {forceDashboard: true});
            setSortData(dataSort);
            setHasStartFilter(false);
        }
    }, [hasStartFilter]);

    useEffect(() => {
        if (activeClick?.item === "/period_statuses" && activeClick?.from ==='#/period_statuses' && !!selectedCampaign) {
            fetchCampaignLeads(sortData);
            resetTableHorizontalScroll();
        }
    }, [activeClick]);

    const processedCampaignIdRef = useRef<number | null>(null);

    useEffect(() => {
        handleReset();
    }, []);

    useEffect(() => {
        return () => {
            dispatch(clearContactsByCampaignIdDashboard());
        };
    }, [dispatch]);

    useEffect(() => {
        if (activeClick?.item === "/leads" && activeClick?.campaignId) {
            const campaignId = activeClick.campaignId;

            if (campaignId && processedCampaignIdRef.current !== campaignId) {
                    setTimeout(() => {
                        setSelectedCampaign(campaignId);
                        selectedCampaignRef.current = campaignId;
                        if (dataTableRef.current) {
                            dataTableRef.current.reset();
                        }
                        setFilterContactName(null);
                        setFilterPhoneNo(null);
                        setFilterPhoneTypeId(null);
                        setFilterDescription(null);
                        setFilterIsDone(null);
                        setFilterIsActive(null);
                        setFilterCallOwner(null);
                        setOrderBy(null);
                        setIsDescFilter(true);
                        setSortOrder(0);
                        setFirst(0);
                        setHasEndFilter(true);
                    }, 100);
                    processedCampaignIdRef.current = campaignId;
            }
        } else if (activeClick?.item === "/leads" && !activeClick?.campaignId && selectedCampaign) {
            fetchCampaignLeads(sortData, {forceDashboard: true});
            resetTableHorizontalScroll();
        } else if (!activeClick?.campaignId) {
            processedCampaignIdRef.current = null;
        }
    }, [activeClick]);

    useEffect(() => {
        if (tabSwitchInProgress.current) {
            tabSwitchInProgress.current = false;
            return;
        }
        if (paginationChangeInProgress.current) {
            paginationChangeInProgress.current = false;
            return;
        }
        if (sortData && sortData.campaignId && hasInitializedPagination.current) {
            if (!isOrderByAllowed(value, sortData?.sort?.orderBy)) return;
            const updatedDataSort = {
                ...sortData,
                pageNumber: campaignContactsPageSize.page,
                pageSize: campaignContactsPageSize.size
            };
            setSortData(updatedDataSort);
            fetchCampaignLeads(updatedDataSort);
        }
    }, [campaignContactsPageSize.page, campaignContactsPageSize.size]);

    useEffect(() => {
        if (!(hasInitializedPagination.current && sortData?.campaignId)) return;
        const defaultOrderByForTab = value === OWNER_LEADS_TAB ? 'OwnerUserName' : 'ContactName';
        tabSwitchInProgress.current = true;
        const dataToFetch = {
            ...sortData,
            sort: { ...sortData.sort, orderBy: defaultOrderByForTab, isDesc: true },
            pageNumber: 1,
            pageSize: 10
        };
        setSortData(dataToFetch);
        setFirst(0);
        setOrderBy(null);
        setIsDescFilter(true);
        setSortOrder(0);
        fetchCampaignLeads(dataToFetch);
        if (campaignContactsPageSize.page !== 1 || campaignContactsPageSize.size !== 10) {
            dispatch(setCampaignContactsPageSize({ page: 1, size: 10 }));
        } else {
            tabSwitchInProgress.current = false;
        }
    }, [value]);

    useEffect(() => {
        selectedCampaignRef.current = selectedCampaign;
    }, [selectedCampaign]);

    useEffect(() => {
        if (tabSwitchInProgress.current) {
            if (hasEndFilter) setHasEndFilter(false);
            return;
        }
        const currentCampaign = selectedCampaign || selectedCampaignRef.current;
        if (hasEndFilter && !!currentCampaign) {
            setFirst(0);
            setPage(1);
            let filter: { key: string; value: string }[] = [];
            const campaignIdValue = currentCampaign ? (typeof currentCampaign === 'string' || typeof currentCampaign === 'number' ? Number(currentCampaign) : Number(currentCampaign?.id || currentCampaign)) : null;

            const resolvedOrderBy = orderBy ?? defaultOrderBy;
            if (!isOrderByAllowed(value, resolvedOrderBy)) {
                setHasEndFilter(false);
                return;
            }
            const dataSort: any = {
                pageNumber: 1,
                pageSize: campaignContactsPageSize.size,
                sort: {
                    orderBy: resolvedOrderBy,
                    isDesc: isDescFilter
                }
            }

            if (campaignIdValue) {
                dataSort.campaignId = campaignIdValue;
            }

            if (filterStatusesId?.value) {
                filter.push(filterStatusesId);
            }
            if (filterStatusName?.value) {
                filter.push(filterStatusName);
            }
            if (filterStatusIds) {
                filterStatusIds.map((item: any) => {
                    filter.push(item);
                })
            }
            if (filterTypeId) {
                filterTypeId.map((item: any) => {
                    filter.push(item);
                })
            }
            if (filterStatusId) {
                dataSort.active = filterStatusId?.value
            }

            if (filterChangeType?.value) {
                filter.push(filterChangeType);
            }

            if (filterContactName?.value) {
                filter.push(filterContactName);
            }
            if (filterPhoneTypeId) {
                filterPhoneTypeId.map((item: any) => {
                    filter.push(item);
                })
            }
            if (filterIsDone) {
                filterIsDone.map((item: any) => {
                    filter.push(item);
                })
            }
            if (filterPhoneNo?.value) {
                filter.push(filterPhoneNo);
            }
            if (filterIsActive?.value) {
                filter.push(filterIsActive);
            }
            if (filterDescription?.value) {
                filter.push(filterDescription);
            }
            if (filterCallOwner) {
                filterCallOwner?.map((item: any) => {
                    filter.push(item);
                })
            }

            if (filterDateTime && filterEndDateTime && !allTimeChecked) {
                let startDate = formatDate(filterDateTime);
                let endDate = formatDate(filterEndDateTime);

                if (startTime && endTime) {
                    let filterStartTime = getTime(startTime);
                    let filterEndTime = getTime(endTime);
                    filter.push({
                        "key": "CreateDateTime",
                        "value": `${startDate} ${filterStartTime},${endDate} ${filterEndTime}`
                    })
                }
            }
            if (filterLastCallDateTime && filterLastCallEndDateTime) {
                let startDate = formatDate(filterLastCallDateTime);
                let endDate = formatDate(filterLastCallEndDateTime);

                if (startLastCallTime && endLastCallTime) {
                    let filterStartTime = getTime(startLastCallTime);
                    let filterEndTime = getTime(endLastCallTime);
                    filter.push({
                        "key": "LastCall",
                        "value": `${startDate} ${filterStartTime},${endDate} ${filterEndTime}`
                    })
                }
            }
            if (filterNewCallDateTime && filterNewCallEndDateTime) {
                let startDate = formatDate(filterNewCallDateTime);
                let endDate = formatDate(filterNewCallEndDateTime);

                if (startNewCallTime && endNewCallTime) {
                    let filterStartTime = getTime(startNewCallTime);
                    let filterEndTime = getTime(endNewCallTime);
                    filter.push({
                        "key": "NewCall",
                        "value": `${startDate} ${filterStartTime},${endDate} ${filterEndTime}`
                    })
                }
            }

            if (filterFinishDateTime && filterFinishEndDateTime && startFinishTime && endFinishTime) {
                let startDate = formatDate(filterFinishDateTime);
                let endDate = formatDate(filterFinishEndDateTime);
                let filterStartTime = getTime(startFinishTime);
                let filterEndTime = getTime(endFinishTime);
                filter.push({
                    key: "TimeEnd",
                    value: `${startDate} ${filterStartTime},${endDate} ${filterEndTime}`
                });
            }

            if (timeInStatusStartTime && timeInStatusEndTime &&
                (!isDefaultTime(timeInStatusStartTime) || !isDefaultTime(timeInStatusEndTime))) {
                const startSeconds = getTotalSeconds(timeInStatusStartTime);
                const endSeconds = getTotalSeconds(timeInStatusEndTime);
                if (startSeconds !== null && endSeconds !== null) {
                    filter.push({
                        key: 'TimeInStatus',
                        value: `${startSeconds},${endSeconds}`
                    });
                }
            }

            if (filter.length > 0) {
                dataSort['filter'] = filter;
            }

            fetchCampaignLeads(dataSort);
            setPage(null);
            setSortData(dataSort);
            setHasEndFilter(false);
        }
    }, [hasEndFilter, selectedCampaign, value, campaignContactsPageSize.size]);

    useEffect(() => {
        if (hasTimeInStatusFilter && !!selectedCampaign) {
            setFirst(0);
            setPage(1);
            let filter: { key: string; value: string }[] = [];
            const campaignIdValue = selectedCampaign ? (typeof selectedCampaign === 'string' || typeof selectedCampaign === 'number' ? Number(selectedCampaign) : Number(selectedCampaign?.id || selectedCampaign)) : null;

            const dataSort: any = {
                pageNumber: 1,
                pageSize: campaignContactsPageSize.size,
                sort: {
                    orderBy: orderBy ?? defaultOrderBy,
                    isDesc: isDescFilter
                }
            }

            if (campaignIdValue) {
                dataSort.campaignId = campaignIdValue;
            }

            if (filterStatusesId?.value) {
                filter.push(filterStatusesId);
            }
            if (filterStatusName?.value) {
                filter.push(filterStatusName);
            }
            if (filterStatusIds) {
                filterStatusIds.map((item: any) => {
                    filter.push(item);
                })
            }
            if (filterTypeId) {
                filterTypeId.map((item: any) => {
                    filter.push(item);
                })
            }
            if (filterStatusId) {
                dataSort.active = filterStatusId?.value
            }

            if (filterChangeType?.value) {
                filter.push(filterChangeType);
            }

            if (filterDateTime && filterEndDateTime && startTime && endTime) {
                let startDate = formatDate(filterDateTime);
                let endDate = formatDate(filterEndDateTime);
                let filterStartTime = getTime(startTime);
                let filterEndTime = getTime(endTime);
                filter.push({
                    key: "TimeStart",
                    value: `${startDate} ${filterStartTime},${endDate} ${filterEndTime}`
                });
            }

            if (filterFinishDateTime && filterFinishEndDateTime && startFinishTime && endFinishTime) {
                let startDate = formatDate(filterFinishDateTime);
                let endDate = formatDate(filterFinishEndDateTime);
                let filterStartTime = getTime(startFinishTime);
                let filterEndTime = getTime(endFinishTime);
                filter.push({
                    key: "TimeEnd",
                    value: `${startDate} ${filterStartTime},${endDate} ${filterEndTime}`
                });
            }

            if (timeInStatusStartTime && timeInStatusEndTime &&
                (!isDefaultTime(timeInStatusStartTime) || !isDefaultTime(timeInStatusEndTime))) {
                const startSeconds = getTotalSeconds(timeInStatusStartTime);
                const endSeconds = getTotalSeconds(timeInStatusEndTime);
                if (startSeconds !== null && endSeconds !== null) {
                    filter.push({
                        key: 'TimeInStatus',
                        value: `${startSeconds},${endSeconds}`
                    });
                }
            }

            if (filter.length > 0) {
                dataSort['filter'] = filter;
            }

            justAppliedTimeInStatusFilter.current = true;
            timeInStatusFilterRequestSent.current = true;

            fetchCampaignLeads(dataSort);

            setTimeout(() => {
                setHasTimeInStatusFilter(false);
                setTimeout(() => {
                    justAppliedTimeInStatusFilter.current = false;
                    timeInStatusFilterRequestSent.current = false;
                }, 200);
            }, 100);
        }
    }, [hasTimeInStatusFilter]);

    const filterClearTemplate = (options: any) => {
        return <Button type="button" icon="pi pi-times" onClick={(e) => {
            if (options.filterClearCallback) {
                options.filterClearCallback(e);
            }
            if (options.field === 'lastCall') {
                setStartLastCallTime(null);
                setEndLastCallTime(null);
                setFilterLastCallDateTime(null);
                setFilterLastCallEndDateTime(null);
            }
            if (options.field === 'newCall') {
                setStartNewCallTime(null);
                setEndNewCallTime(null);
                setFilterNewCallDateTime(null);
                setFilterNewCallEndDateTime(null);
            }
            if (options.field === 'createdDateTime') {
                setStartTime(null);
                setEndTime(null);
                setFilterDateTime(null);
                setFilterEndDateTime(null);
            }
        }} className="p-button-secondary"></Button>;
    }

    const filterApplyTemplate = (options: {
        filterApplyCallback: React.MouseEventHandler<HTMLButtonElement> | undefined;
        field: string;
    }) => {
        const handleClick = () => {
            if (options.field === 'timeStart') {
                if (!startTime) setStartTime(getDefaultStartTime());
                if (!endTime) setEndTime(getDefaultEndTime());
            }

            if (options.field === 'timeEnd') {
                if (!startFinishTime) setStartFinishTime(getDefaultStartTime());
                if (!endFinishTime) setEndFinishTime(getDefaultEndTime());

                if (!filterFinishDateTime) setFilterFinishDateTime(getDefaultStartDate());
                if (!filterFinishEndDateTime) setFilterFinishEndDateTime(getDefaultEndDate());

                setHasEndFilter(true);

                if (dataTableRef.current) {
                    const filterValue = endFinishTime || getDefaultEndTime();
                    dataTableRef.current.filter(filterValue, 'timeEnd', 'contains');
                }
            }

            if (options.field === 'timeInStatus') {
                if (!timeInStatusStartTime) setTimeInStatusStartTime(getDefaultStartTime());
                if (!timeInStatusEndTime) setTimeInStatusEndTime(getDefaultStartTime());

                justAppliedTimeInStatusFilter.current = true;
                setHasTimeInStatusFilter(true);

                if (dataTableRef.current) {
                    const filterValue = timeInStatusStartTime || getDefaultStartTime();
                    dataTableRef.current.filter(filterValue, 'timeInStatus', 'contains');
                }
                return;
            }

            if (options.filterApplyCallback) {
                options.filterApplyCallback({} as any);
            }
        };

        return <Button type="button" icon="pi pi-check" onClick={handleClick} disabled={
            (options.field === 'createdDateTime' && (!startTime || !endTime || !filterDateTime || !filterEndDateTime)) ||
            (options.field === 'lastCall' && (!startLastCallTime || !endLastCallTime || !filterLastCallDateTime || !filterLastCallEndDateTime)) ||
            (options.field === 'newCall' && (!startNewCallTime || !endNewCallTime || !filterNewCallDateTime || !filterNewCallEndDateTime))
        } className=""></Button>
    }

    const dataTableRef = useRef<any>(null);
    const phoneNoFilterValueRef = useRef<string>('');
    const [phoneNoFilterInputValue, setPhoneNoFilterInputValue] = useState<string | undefined>(undefined);

    const handleReset = () => {
        dataTableRef?.current?.reset();
        customFilter();
        setSortOrder(0);
        setTimeInStatusStartTime(getDefaultStartTime());
        setTimeInStatusEndTime(getDefaultStartTime());
        setHasTimeInStatusFilter(false);

        setStartTime(getDefaultStartTime());
        setEndTime(getDefaultEndTime());
        setFilterDateTime(getDefaultStartDate());
        setFilterEndDateTime(getDefaultEndDate());
        setAllTimeChecked(false);
        setHasStartFilter(false);

        dispatch(setCampaignContactsPageSize({
            page: 1,
            size: 10
        }))
        dispatch(clearCampaignContacts());
        dispatch(clearContactsByCampaignIdDashboard());
        prevLeadsDashboardKeyRef.current = '';
        setFirst(0);
        setSelectedCampaign(null);
    };

    const onPageChange = (event: any) => {
        setFirst(event.first);
        const newPage = event.page + 1;
        const newSize = event.rows;
        dispatch(setCampaignContactsPageSize({
            page: newPage,
            size: newSize
        }));
        if (sortData?.campaignId && hasInitializedPagination.current) {
            paginationChangeInProgress.current = true;
            const updatedSortData = {
                ...sortData,
                pageNumber: newPage,
                pageSize: newSize
            };
            setSortData(updatedSortData);
            fetchCampaignLeads(updatedSortData);
        }
    };

    const customSort = (event: any) => {
        const handleAction = (sortField: string) => {
            let newOrderBy: string;
            switch (sortField) {
                case 'contactName':
                    newOrderBy = 'ContactName';
                    break;
                case 'phoneTypeId':
                    newOrderBy = 'PhoneTypeId';
                    break;
                case 'CountCalled':
                    newOrderBy = 'CountCalled';
                    break;
                case 'IsDone':
                    newOrderBy = 'IsDone';
                    break;
                case 'IsActive':
                    newOrderBy = 'IsActive';
                    break;
                case 'CreateDateTime':
                    newOrderBy = 'CreateDateTime';
                    break;
                case 'NewCall':
                    newOrderBy = 'NewCall';
                    break;
                case 'LastCall':
                    newOrderBy = 'LastCall';
                    break;
                case 'OwnerUserName':
                    newOrderBy = 'OwnerUserName';
                    break;
                case 'statusName':
                    newOrderBy = 'StatusName';
                    break;
                case 'timeStart':
                    newOrderBy = 'TimeStart';
                    break;
                case 'TimeEnd':
                    newOrderBy = 'TimeEnd';
                    break;
                case 'TimeInStatus':
                    newOrderBy = 'TimeInStatus';
                    break;
                case 'AllCountContacts':
                    newOrderBy = 'AllCountContacts';
                    break;
                case 'IsActiveCount':
                    newOrderBy = 'IsActiveCount';
                    break;
                case 'InactiveCount':
                    newOrderBy = 'InactiveCount';
                    break;
                case 'SuccessCount':
                    newOrderBy = 'SuccessCount';
                    break;
                case 'UnsuccessCount':
                    newOrderBy = 'UnsuccessCount';
                    break;
                case 'UnsuccessActiveCount':
                    newOrderBy = 'UnsuccessActiveCount';
                    break;
                case 'UnsuccessInactiveCount':
                    newOrderBy = 'UnsuccessInactiveCount';
                    break;
                case 'Total':
                case 'Active':
                case 'Inactive':
                case 'AttemptsDone':
                case 'Success':
                case 'Unsuccess':
                    newOrderBy = sortField;
                    break;
                case 'CoincidenceCount':
                    newOrderBy = 'CoincidenceCount';
                    break;
                default:
                    if (typeof sortField === 'string' && sortField.startsWith('PhoneType_')) {
                        newOrderBy = sortField;
                        break;
                    }
                    return;
            }

            setOrderBy(newOrderBy);
            setIsDescFilter(!isDescFilter);
            setSortOrder(sortOrder === 1 ? -1 : 1);

            if (selectedCampaignRef.current) {
                setHasEndFilter(true);
            }
        };

        let sortField = event?.sortField;
        if (sortField) {
            handleAction(sortField);
        }
    };


    const customFilter = (event?: any) => {
        if (event && event.filters) {
            const currentFilterDateTime = filterDateTime;
            const currentFilterLastCallDateTime = filterLastCallDateTime;
            const currentFilterNewCallDateTime = filterNewCallDateTime;

            const {id, statusName, active, typeId, changeType, phoneNo, contactName, phoneTypeId, description, result, isActive, createdDateTime, lastCall, newCall, ownerUserName} = event.filters;
            const filters = event.filters;
            const setFilter = (filterName: string, key: string, defaultValue?: any | null) => {
                if (!filters[filterName]) {
                    return defaultValue;
                }

                const value = filters[filterName].value || filters[filterName].constraints?.[0]?.value;
                if (!value) {
                    return defaultValue;
                }

                if (Array?.isArray(value)) {
                    return value.map((item) => ({key, value: String(item)}));
                } else {
                    return {key, value: String(value)};
                }
            };

            if (contactName) {
                setFilterContactName(contactName ? {key: 'ContactName', value: contactName.constraints[0].value} : null);
            }
            if (phoneNo) {
                setFilterPhoneNo(phoneNo ? {key: 'PhoneNo', value: phoneNo.constraints[0].value} : null);
            }
            if (description) {
                setFilterDescription(description ? {key: 'Description', value: description.constraints[0].value} : null);
            }
            if (isActive) {
                setFilterIsActive(isActive ? {key: 'IsActive', value: isActive.value} : null);
            }
            if (createdDateTime !== undefined && createdDateTime !== null) {
                if (createdDateTime.value !== undefined && createdDateTime.value !== null) {
                    setFilterDateTime(createdDateTime.value);
                }
            } else {
                if (currentFilterDateTime !== null && currentFilterDateTime !== false) {
                    setFilterDateTime(currentFilterDateTime);
                }
            }
            if (lastCall !== undefined && lastCall !== null) {
                if (lastCall.value !== undefined && lastCall.value !== null) {
                    setFilterLastCallDateTime(lastCall.value);
                }
            } else {
                if (currentFilterLastCallDateTime !== null && currentFilterLastCallDateTime !== false) {
                    setFilterLastCallDateTime(currentFilterLastCallDateTime);
                }
            }
            if (newCall !== undefined && newCall !== null) {
                if (newCall.value !== undefined && newCall.value !== null) {
                    setFilterNewCallDateTime(newCall.value);
                }
            } else {
                if (currentFilterNewCallDateTime !== null && currentFilterNewCallDateTime !== false) {
                    setFilterNewCallDateTime(currentFilterNewCallDateTime);
                }
            }
            if (ownerUserName) {
                setFilterCallOwner(setFilter('ownerUserName', 'OwnerUserName', []));
            }
            if (phoneTypeId?.value) {
                setFilterPhoneTypeId(phoneTypeId.value.map((item: any) => ({key: 'PhoneTypeId', value: String(item)})));
            } else if (phoneTypeId !== undefined) {
                setFilterPhoneTypeId(null);
            }
            if (result?.value) {
                setFilterIsDone(result.value.map((item: any) => ({key: 'Result', value: String(item)})));
            } else if (result !== undefined) {
                setFilterIsDone(null);
            }

            setFilterStatusesId(id ? {key: 'Id', value: id.constraints[0].value} : null);
            setFilterStatusId(active ? {key: 'Active', value: active.value ? active.value.code : null} : null);
            if (typeId?.value) {
                setFilterTypeId(typeId.value.map((item: any) => ({key: 'TypeId', value: String(item)})));
            } else {
                setFilterTypeId(null);
            }
            if (statusName?.value) {
                setFilterStatusIds(statusName.value.map((item: any) => ({key: 'StatusId', value: String(item)})));
            } else {
                setFilterStatusIds(null);
            }
            setFilterChangeType(changeType?.value ? {key: 'ChangeType', value: changeType.value} : null);

            if (selectedCampaignRef.current) {
                setHasEndFilter(true);
            }
        } else if (event && !event.filters) {
            if (selectedCampaignRef.current) {
                setHasEndFilter(true);
            }
        } else {
            setFilterContactName(null);
            setFilterPhoneNo(null);
            setFilterPhoneTypeId(null);
            setFilterDescription(null);
            setFilterIsDone(null);
            setFilterIsActive(null);
            setFilterCallOwner(null);
            setFilterStatusesId(null);
            setFilterTypeId(null);
            setFilterStatusName(null);
            setFilterStatusId(null);
            setFilterStatusIds(null);
            setFilterChangeType(null);
            setOrderBy(null);
            setIsDescFilter(true);
        }
    };

    const isMobile = () => {
        return windowWidth <= 1248;
    }


    const getActiveFilterClassName = (baseClassName: string, isActive: boolean) => {
        const result = isActive ? `${baseClassName} active-time-filter` : baseClassName;
        return result;
    };

    const isTimeFilterActive = (filterName: string) => {
        let result = false;
        switch (filterName) {
            case 'duration':
                result = duration;
                break;
            case 'timeInStatus':
                result = hasTimeInStatusFilter;
                break;
            default:
                result = false;
        }

        return result;
    };

    const getPhoneTypeName = (phoneTypeId: number) => {
        const type = campaignDirectoriesPhoneType?.find((item: any) => item.dictionaryId === phoneTypeId);
        return type ? type.name : '- - -';
    }

    const headerTemplate = (data: any) => {
        return (
            <div className="max-w-16rem">
                <div className="field col-12 md:col-12 flex align-items-center m-0">
                    {data.main?.pathSegment ?
                        <a target="_blank" className="mb-2" href={`${data.main.pathSegment}`}>{data.main.contactName}</a> :
                        <p className="m-0 mb-2">{data.main?.contactName}</p>}
                </div>
            </div>
        );
    }

    const headerTemplate2 = (data: any) => {
        return (
            <div className="max-w-16rem">
                <div className="field col-12 md:col-12 flex align-items-center m-0">
                    {data?.contactPhoneName}
                </div>
            </div>
        );
    }

    const typeNumberTemplate = (data: any) => {
        return (
            <React.Fragment>
            </React.Fragment>
        )
    }

    const typeNumberTemplateExp = (data: any) => {
        return (
            <React.Fragment>
                <span>{getPhoneTypeName(data.phoneTypeId)}</span>
            </React.Fragment>
        )
    }

    const calledTemplate = (data: any) => {
        return (
            <React.Fragment>
                <div className="pl-2">
                </div>
            </React.Fragment>
        )
    }

    const calledTemplateExp = (data: any) => {
        return (
            <React.Fragment>
                <div className="pl-2">
                    <span>{data?.countCalled + ' ' + `/` + ' ' + data?.countCalls}</span>
                </div>
            </React.Fragment>
        )
    }

    const dateTemplate = (data: any, field: any) => {
        let dateValue;

        switch (field) {
            case 'createDateTime':
                dateValue = data?.main?.createDateTime || data?.createDateTime;
                break;
            case 'lastCall':
                dateValue = data?.main?.lastCall || data?.lastCall;
                break;
            case 'newCall':
                dateValue = data?.main?.newCall || data?.newCall;
                break;
            default:
                dateValue = null;
        }

        return (
            <React.Fragment>
                <div className="pl-2 pt-0 pr-3">
                    <span>{dateValue ? formatDateTime(dateValue) : '- - -'}</span>
                </div>
            </React.Fragment>
        )
    }

    const dateTemplate2 = (data: any, field: any) => {
        let dateValue;

        switch (field) {
            case 'createDateTime':
                dateValue = data?.main?.createDateTime || data?.createDateTime;
                break;
            case 'lastCall':
                dateValue = data?.main?.lastCall || data?.lastCall;
                break;
            case 'newCall':
                dateValue = data?.main?.newCall || data?.newCall;
                break;
            default:
                dateValue = null;
        }

        return (
            <React.Fragment>
                <div className="pl-3 pt-0 pr-3">
                    <span>{dateValue ? formatDateTime(dateValue) : '- - -'}</span>
                </div>
            </React.Fragment>
        )
    }



    const isActiveTemplate = (data: any) => {
        return (
            <React.Fragment>
                <div className="pl-2">
                    <span>{(data?.main?.isActive !== undefined ? data.main.isActive : data?.isActive) ? 'Активний' : 'Неактивний'}</span>
                </div>
            </React.Fragment>
        )
    }

    const allContactsTemplate = (data: any) => {
        const count = data?.total ? readOwnerMetric(data.total, 'total') : (data?.allCountContacts ?? data?.contacts?.length ?? '');
        return (
            <div className="pl-2">
                <span>{count}</span>
            </div>
        )
    }

    const activeContactsTemplate = (data: any) => {
        const activeCount = data?.total
            ? readOwnerMetric(data.total, 'active')
            : (data?.isActiveCount ?? data?.contacts?.filter((c: any) => c.isActive === true)?.length ?? '');
        return (
            <div className="pl-2">
                <span>{activeCount}</span>
            </div>
        )
    }

    const inactiveContactsTemplate = (data: any) => {
        const inactiveCount = data?.total
            ? readOwnerMetric(data.total, 'inactive')
            : (data?.inactiveCount ?? data?.contacts?.filter((c: any) => c.isActive === false)?.length ?? '');
        return (
            <div className="pl-2">
                <span>{inactiveCount}</span>
            </div>
        )
    }

    const ownerAttemptsTemplate = (data: any) => {
        return (
            <div className="pl-2">
                <span>{formatOwnerAttempts(data?.total)}</span>
            </div>
        )
    }

    const successContactsTemplate = (data: any) => {
        const successCount = data?.total
            ? readOwnerMetric(data.total, 'success')
            : (data?.successCount ?? data?.contacts?.filter((c: any) => c.result === 'Success')?.length ?? '');
        return (
            <div className="pl-2">
                <span>{successCount}</span>
            </div>
        )
    }

    const ownerUnsuccessTemplate = (data: any) => {
        const unsuccessCount = data?.total
            ? readOwnerMetric(data.total, 'unsuccess')
            : (data?.unsuccessCount ?? '');
        return (
            <div className="pl-2">
                <span>{unsuccessCount}</span>
            </div>
        )
    }

    const getLeadPhoneTypeCount = (data: any, column: any) => {
        const typeStats = asStatArray(data?.phoneTypes || data?.types || data?.phoneTypeStatistic || data?.main?.phoneTypes);
        const findTypeStat = () => typeStats.find((item: any) => {
            const itemId = item?.phoneTypeId ?? item?.typeId ?? item?.dictionaryId ?? item?.id;
            const itemName = (item?.name || item?.phoneTypeName || item?.typeName || '').toString().trim().toLowerCase();
            if (column.isCoincidence) {
                return itemName === COINCIDENCE_LABEL.toLowerCase();
            }
            if (column.phoneTypeId != null && itemId != null && Number(itemId) === Number(column.phoneTypeId)) {
                return true;
            }
            return itemName === (column.name || '').toString().trim().toLowerCase();
        });

        if (column.isCoincidence) {
            const coincidenceValue = pickStatValue(data, ['coincidenceCount', 'coincidence', 'matches', 'matchCount']);
            if (coincidenceValue != null) return coincidenceValue;
            const coincidenceStat = findTypeStat();
            const coincidenceStatValue = pickStatValue(coincidenceStat, ['allCountContacts', 'count', 'value', 'total']);
            if (coincidenceStatValue != null) return coincidenceStatValue;
            return 0;
        }

        const typeStat = findTypeStat();
        const typeValue = pickStatValue(typeStat, ['allCountContacts', 'count', 'value', 'total', 'leadsCount']);
        if (typeValue != null) return typeValue;

        if (Array.isArray(data?.contacts) && column.phoneTypeId != null) {
            return data.contacts.filter((contact: any) => Number(contact?.phoneTypeId) === Number(column.phoneTypeId)).length;
        }

        const flattenedValue = pickStatValue(data, [
            column.name,
            `phoneType${column.phoneTypeId}`,
            `phoneType_${column.phoneTypeId}`,
        ]);
        return flattenedValue ?? 0;
    };

    const phoneTypeCountTemplate = (data: any, column: any) => {
        return (
            <React.Fragment>
                <div className="pl-2">
                    <span>{getLeadPhoneTypeCount(data, column)}</span>
                </div>
            </React.Fragment>
        )
    }

    const ownerUserNameTemplate = (data: any) => {
        return (
            <React.Fragment>
                <div className="pl-0">
                    <span>{data?.main?.ownerUserName || data?.ownerUserName || '- - -'}</span>
                </div>
            </React.Fragment>
        )
    }

    const ownerUserName2Template = (data: any) => {
        return (
            <React.Fragment>
                <div className="pl-0 py-2">
                    <span>{data?.main?.ownerUserName || data?.ownerUserName || '- - -'}</span>
                </div>
            </React.Fragment>
        )
    }

    const statusTemplate = (data: any) => {
        return (
            <React.Fragment>
            </React.Fragment>
        )
    }

    const statusTemplateExp = (data: any) => {
        return (
            <div className="ml-2">
                {data?.result !== 'NoCall' && (<span className={`customer-badge ${data?.result === 'Success' ? 'status-6' : 'status-5'}`} style={{borderRadius: `6px`, minWidth: '93px'}}>{data.isDone ? 'success' : 'unsuccess'}</span>)}
            </div>
        )
    }

    const replyIcon = () => {
        return <div className="flex justify-content-center" style={{minWidth: '30px'}}><i className="pi pi-reply mr-2 py-2" style={{fontSize: '1rem', transform: 'rotate(270deg)'}}></i></div>
    }

    const ownerRowExpansionTemplate = (data: any) => {
        const types = Array.isArray(data?.types) ? data.types : [];
        return <OwnerTypesDashboard types={types}/>;
    }

    const rowExpansionTemplate = (data: any) => {
        return (
                <div style={{width: '100%', display: 'block', margin: 0, padding: 0}}>
                    <style>{`

                    `}</style>
                    <DataTable
                        value={data?.contacts || []}
                        ref={dataTableRef}
                        filters={clearLeadsFilters}
                        sortMode="single"
                        onSort={customSort}
                        className="dialogLeadsExpansion campaignLeadsExpansion px-0"
                        style={{width: '100%'}}
                        onFilter={customFilter}
                        loading={contactsLoader}
                        dataKey="contact"
                    >
                    <Column header className="replyColumn" headerStyle={{display: 'none', width: '3rem'}} style={{width: '35px', maxWidth: '35px', paddingLeft: 0, paddingRight: 0, paddingTop: '5px', paddingBottom:'5px'}} bodyStyle={{justifyContent:'end'}} body={replyIcon}/>
                    <Column header headerStyle={{display: 'none', minWidth: '280px', width: '100%'}} bodyStyle={{minWidth: '280px', width: '100%'}} body={headerTemplate2}
                            style={{cursor: 'pointer', paddingLeft: 0, paddingRight: 0, minWidth: '200px', width: '100%'}}
                    />
                    <Column header headerStyle={{display: 'none', minWidth: '200px', width: '100%'}} bodyStyle={{minWidth: '200px', width: '100%'}} body={ownerUserNameTemplate}
                            style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}/>
                    <Column headerStyle={{display: 'none', minWidth: '200px', width: '100%'}} bodyStyle={{minWidth: '200px', width: '100%'}} body={isActiveTemplate}
                            style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}/>
                    <Column header headerStyle={{display: 'none', minWidth: '200px', width: '100%'}} bodyStyle={{minWidth: '200px', width: '100%'}} field="phoneNo" style={{minWidth: '200px', width: '100%'}}/>
                    <Column header headerStyle={{display: 'none', minWidth: '200px', width: '100%'}} bodyStyle={{minWidth: '200px', width: '100%'}} body={typeNumberTemplateExp}
                            field="phoneTypeId" style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}/>
                    <Column header headerStyle={{display: 'none', minWidth: '200px', width: '100%'}} bodyStyle={{minWidth: '200px', width: '100%'}} body={(e) => dateTemplate(e, 'lastCall')}
                            style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}
                    />
                    <Column header headerStyle={{display: 'none', minWidth: '200px', width: '100%'}} bodyStyle={{minWidth: '200px', width: '100%'}} body={calledTemplateExp}
                            style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}/>
                    <Column headerStyle={{display: 'none', minWidth: '200px', width: '100%'}} bodyStyle={{minWidth: '200px', width: '100%'}} header body={(e) => dateTemplate2(e, 'newCall')}
                            sortField="newCall" style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}
                    />
                    <Column headerStyle={{display: 'none', minWidth: '200px', width: '100%'}} bodyStyle={{minWidth: '200px', width: '100%'}}  header body={statusTemplateExp}
                            style={{cursor: 'pointer', paddingLeft: '10px', minWidth: '200px', width: '100%'}}
                    />
                    <Column header headerStyle={{display: 'none', minWidth: '200px', width: '100%'}} bodyStyle={{minWidth: '200px', width: '100%'}} body={(e) => dateTemplate2(e, 'createDateTime')}
                            style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}
                    />
                    {/* {leadPhoneTypeColumns.map((column) => (
                        <Column key={column.sortField}
                                header
                                headerStyle={{display: 'none', minWidth: '160px', width: '100%'}}
                                bodyStyle={{minWidth: '160px', width: '100%'}}
                                style={{minWidth: '160px', width: '100%'}}/>
                    ))} */}
                    </DataTable>
                </div>
        );
    }

    const header = (field: string, sortField: string) => {
        const sorted = sortField === orderBy;
        const sortIcon = classNames('p-sortable-column-icon', {
            'pi pi-sort-amount-up-alt': sorted && sortOrder === 1,
            'pi pi-fw pi-sort-amount-down': sorted && sortOrder === -1,
            'pi pi-fw pi-sort-alt': sortOrder === 0 || !sorted
        });

        return (
            <div className="flex align-items-center" onClick={() => customSort({'sortField': sortField})}>
                {field}
                <div className={sortIcon}></div>
            </div>
        );
    };

    const phoneNoFilterTemplate = (options: any) => {
        const displayValue = phoneNoFilterInputValue !== undefined ? phoneNoFilterInputValue : (options.value ?? '');
        phoneNoFilterValueRef.current = displayValue;
        const allowKey = (e: React.KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'v' || e.code === 'KeyV')) {
                e.stopPropagation();
                return true;
            }
            if (/[0-9]/.test(e.key)) return true;
            if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return true;
            if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'x'].includes(e.key.toLowerCase())) return true;
            e.preventDefault();
            return false;
        };
        const handlePaste = (e: React.ClipboardEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const pasted = (e.clipboardData.getData('text') ?? '').replace(/\D/g, '');
            setPhoneNoFilterInputValue(pasted);
            phoneNoFilterValueRef.current = pasted;
            options.filterCallback(pasted);
        };
        return (
            <InputText
                value={displayValue}
                onChange={(e: any) => {
                    const v = (e.target.value ?? '').replace(/\D/g, '');
                    setPhoneNoFilterInputValue(v);
                    phoneNoFilterValueRef.current = v;
                    options.filterCallback(v);
                }}
                onKeyDown={allowKey}
                onKeyDownCapture={(e: React.KeyboardEvent) => {
                    if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'v' || e.code === 'KeyV')) e.stopPropagation();
                }}
                onPasteCapture={handlePaste}
                placeholder="Введіть номер"
                className="p-column-filter"
                inputMode="numeric"
            />
        );
    };

    const typeFilterTemplate = (options: any) => {
        return (
            <>
                <div className="mb-3 text-bold">Оберіть тип</div>
                <MultiSelect
                    filter
                    value={options.value}
                    options={campaignDirectoriesPhoneType && campaignDirectoriesPhoneType.map((item: { dictionaryId: number; }) => item.dictionaryId)}
                    itemTemplate={typeItemTemplate}
                    selectedItemTemplate={selectedTypeTemplate}
                    onChange={(e: any) => options.filterCallback(e.value)}
                    placeholder="нічого не обрано"
                    className="p-column-filter"
                    onMouseDown={() => {
                        if (hasUsersAccess) {
                            dispatch(getUsers(jwtToken?.jwtToken));
                        }
                    }}
                />
            </>
        );
    };

    const isActiveFilterTemplate = (options: any) => {
        return (
            <>
                <div className="mb-3 text-bold">Оберіть тип</div>
                <Dropdown
                    filter
                    options={activeDict}
                    optionLabel="value"
                    optionValue="code"
                    value={options.value}
                    onChange={(e: any) => options.filterCallback(e.value)}
                    placeholder="нічого не обрано"
                    className="p-column-filter"
                />
            </>
        );
    };

    const lastCallDateFilterTemplate = (options: any) => {
        return <>
            <span className="title">Виберіть дату</span>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>З*</span>
                <Calendar className="mt-2" value={filterLastCallDateTime ? filterLastCallDateTime : null} onChange={(e: any) => {
                    options.filterCallback(e.value, options.index);
                    setFilterLastCallDateTime(e.value);
                }} onInput={(e: any) => {
                    const input = e.target as HTMLInputElement;
                    const digits = input.value.replace(/\D/g, "").slice(0, 8);
                    const dd = digits.slice(0, 2);
                    const mm = digits.slice(2, 4);
                    const yyyy = digits.slice(4, 8);
                    const display = (dd || "") + (digits.length > 2 ? "/" + mm : "") + (digits.length > 4 ? "/" + yyyy : "");
                    const newCaret = digits.length + (digits.length > 2 ? 1 : 0) + (digits.length > 4 ? 1 : 0);
                    if (input.value !== display) {
                        input.value = display;
                        input.setSelectionRange(newCaret, newCaret);
                    }
                    if (digits.length === 8) {
                        const day = parseInt(dd, 10);
                        const month = parseInt(mm, 10) - 1;
                        const year = parseInt(yyyy, 10);
                        const parsed = new Date(year, month, day);
                        const valid = !isNaN(parsed.getTime()) && parsed.getDate() === day && parsed.getMonth() === month && parsed.getFullYear() === year;
                        setFilterLastCallDateTime(valid ? parsed : null);
                    }
                }} onFocus={(e: any) => {
                    if (filterLastCallDateTime) {
                        setFilterLastCallDateTime(null);
                    }
                }} onBlur={(e: any) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    if (digits.length !== 8) {
                        e.target.value = "";
                        setFilterLastCallDateTime(null);
                    }
                }} minDate={new Date(2023, 5, 1)} maxDate={new Date()}
                          dateFormat="dd/mm/yy" placeholder="дд/мм/рр"/>
            </div>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>По*</span>
                <Calendar className="mt-2" value={filterLastCallEndDateTime ? filterLastCallEndDateTime : null} onChange={(e: any) => {
                    setFilterLastCallEndDateTime(e.value)
                }} onInput={(e: any) => {
                    const input = e.target as HTMLInputElement;
                    const digits = input.value.replace(/\D/g, "").slice(0, 8);
                    const dd = digits.slice(0, 2);
                    const mm = digits.slice(2, 4);
                    const yyyy = digits.slice(4, 8);
                    const display = (dd || "") + (digits.length > 2 ? "/" + mm : "") + (digits.length > 4 ? "/" + yyyy : "");
                    const newCaret = digits.length + (digits.length > 2 ? 1 : 0) + (digits.length > 4 ? 1 : 0);
                    if (input.value !== display) {
                        input.value = display;
                        input.setSelectionRange(newCaret, newCaret);
                    }
                    if (digits.length === 8) {
                        const day = parseInt(dd, 10);
                        const month = parseInt(mm, 10) - 1;
                        const year = parseInt(yyyy, 10);
                        const parsed = new Date(year, month, day);
                        const valid = !isNaN(parsed.getTime()) && parsed.getDate() === day && parsed.getMonth() === month && parsed.getFullYear() === year;
                        setFilterLastCallEndDateTime(valid ? parsed : null);
                    }
                }} onFocus={(e: any) => {
                    if (filterLastCallEndDateTime) {
                        setFilterLastCallEndDateTime(null);
                    }
                }} onBlur={(e: any) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    if (digits.length !== 8) {
                        e.target.value = "";
                        setFilterLastCallEndDateTime(null);
                    }
                }} minDate={new Date(2023, 5, 1)} maxDate={new Date()}
                          dateFormat="dd/mm/yy" placeholder="дд/мм/рр"/>
            </div>
            <div className="flex max-w-15rem">
                <div className="md:col-6">
                    <div className="time-item">
                        <span className="title">З*</span>
                        <Calendar
                            appendTo="self"
                            id="icon"
                            value={startLastCallTime ? startLastCallTime : null}
                            timeOnly
                            mask="99:99"
                            onChange={(e) => setStartLastCallTime(e.value)}
                            hourFormat="24"
                        />
                    </div>
                </div>
                <div className="md:col-6">
                    <div className="time-item">
                        <span className="title">По*</span>
                        <Calendar
                            appendTo="self"
                            id="icon"
                            value={endLastCallTime ? endLastCallTime : null}
                            required
                            timeOnly
                            mask="99:99"
                            onChange={(e) => setEndLastCallTime(e.value)}
                            hourFormat="24"
                        />
                    </div>
                </div>
            </div>
        </>
    };

    const newCallDateFilterTemplate = (options: any) => {
        return <>
            <span className="title">Виберіть дату</span>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>З*</span>
                <Calendar className="mt-2" value={filterNewCallDateTime ? filterNewCallDateTime : null} onChange={(e: any) => {
                    options.filterCallback(e.value, options.index);
                    setFilterNewCallDateTime(e.value)
                }} onInput={(e: any) => {
                    const input = e.target as HTMLInputElement;
                    const digits = input.value.replace(/\D/g, "").slice(0, 8);
                    const dd = digits.slice(0, 2);
                    const mm = digits.slice(2, 4);
                    const yyyy = digits.slice(4, 8);
                    const display = (dd || "") + (digits.length > 2 ? "/" + mm : "") + (digits.length > 4 ? "/" + yyyy : "");
                    const newCaret = digits.length + (digits.length > 2 ? 1 : 0) + (digits.length > 4 ? 1 : 0);
                    if (input.value !== display) {
                        input.value = display;
                        input.setSelectionRange(newCaret, newCaret);
                    }
                    if (digits.length === 8) {
                        const day = parseInt(dd, 10);
                        const month = parseInt(mm, 10) - 1;
                        const year = parseInt(yyyy, 10);
                        const parsed = new Date(year, month, day);
                        const valid = !isNaN(parsed.getTime()) && parsed.getDate() === day && parsed.getMonth() === month && parsed.getFullYear() === year;
                        setFilterNewCallDateTime(valid ? parsed : null);
                    }
                }} onFocus={(e: any) => {
                    if (filterNewCallDateTime) {
                        setFilterNewCallDateTime(null);
                    }
                }} onBlur={(e: any) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    if (digits.length !== 8) {
                        e.target.value = "";
                        setFilterNewCallDateTime(null);
                    }
                }} minDate={new Date(2023, 5, 1)} maxDate={new Date()}
                          dateFormat="dd/mm/yy" placeholder="дд/мм/рр"/>
            </div>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>По*</span>
                <Calendar className="mt-2" value={filterNewCallEndDateTime ? filterNewCallEndDateTime : null} onChange={(e: any) => {
                    setFilterNewCallEndDateTime(e.value)
                }} onInput={(e: any) => {
                    const input = e.target as HTMLInputElement;
                    const digits = input.value.replace(/\D/g, "").slice(0, 8);
                    const dd = digits.slice(0, 2);
                    const mm = digits.slice(2, 4);
                    const yyyy = digits.slice(4, 8);
                    const display = (dd || "") + (digits.length > 2 ? "/" + mm : "") + (digits.length > 4 ? "/" + yyyy : "");
                    const newCaret = digits.length + (digits.length > 2 ? 1 : 0) + (digits.length > 4 ? 1 : 0);
                    if (input.value !== display) {
                        input.value = display;
                        input.setSelectionRange(newCaret, newCaret);
                    }
                    if (digits.length === 8) {
                        const day = parseInt(dd, 10);
                        const month = parseInt(mm, 10) - 1;
                        const year = parseInt(yyyy, 10);
                        const parsed = new Date(year, month, day);
                        const valid = !isNaN(parsed.getTime()) && parsed.getDate() === day && parsed.getMonth() === month && parsed.getFullYear() === year;
                        setFilterNewCallEndDateTime(valid ? parsed : null);
                    }
                }} onFocus={(e: any) => {
                    if (filterNewCallEndDateTime) {
                        setFilterNewCallEndDateTime(null);
                    }
                }} onBlur={(e: any) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    if (digits.length !== 8) {
                        e.target.value = "";
                        setFilterNewCallEndDateTime(null);
                    }
                }} minDate={new Date(2023, 5, 1)} maxDate={new Date()}
                          dateFormat="dd/mm/yy" placeholder="дд/мм/рр"/>
            </div>
            <div className="flex max-w-15rem">
                <div className="md:col-6">
                    <div className="time-item">
                        <span className="title">З*</span>
                        <Calendar
                            appendTo="self"
                            id="icon"
                            value={startNewCallTime ? startNewCallTime : null}
                            timeOnly
                            mask="99:99"
                            onChange={(e) => setStartNewCallTime(e.value)}
                            hourFormat="24"
                        />
                    </div>
                </div>
                <div className="md:col-6">
                    <div className="time-item">
                        <span className="title">По*</span>
                        <Calendar
                            appendTo="self"
                            id="icon"
                            value={endNewCallTime ? endNewCallTime : null}
                            required
                            timeOnly
                            mask="99:99"
                            onChange={(e) => setEndNewCallTime(e.value)}
                            hourFormat="24"
                        />
                    </div>
                </div>
            </div>
        </>
    };

    const isDoneSelect = [
        {name: 'Unsuccess', code: 'Unsuccess'},
        {name: 'Success', code: 'Success'},
        {name: 'NoCall', code: 'NoCall'},
    ];

    const isDoneFilterTemplate = (options: any) => {
        return (
            <>
                <div className="mb-3 text-bold">Оберіть тип</div>
                <MultiSelect
                    filter
                    value={options.value}
                    options={isDoneSelect}
                    onChange={(e: any) => options.filterCallback(e.value)}
                    placeholder="нічого не обрано"
                    optionLabel="name"
                    optionValue="code"
                    className="p-column-filter"
                />
            </>
        );
    };

    const selectedTypeTemplate = (option: any) => {
        let typeName = campaignDirectoriesPhoneType.map((item: { dictionaryId: number; name: any; }) => {
            if (item.dictionaryId === option) {
                return item.name
            }
        })
        if (option) {
            return (
                <span style={{marginLeft: '.5em', verticalAlign: 'middle'}} className="image-text">
                    {typeName}
                </span>
            );
        }

        return "Оберіть тип";
    }

    const typeItemTemplate = (option: any) => {
        let typeName = campaignDirectoriesPhoneType.map((item: { dictionaryId: number; name: any; }) => {
            if (item.dictionaryId === option) {
                return item.name
            }
        })
        return (
            <div className="p-multiselect-representative-option">
                <span style={{marginLeft: '.5em', verticalAlign: 'middle'}} className="image-text">
                    {typeName}
                </span>
            </div>
        );
    }

    const usersDataWithFullName = usersData?.map((userData: any) => {
        return {
            ...userData,
            fullName: `${userData?.lastName} ${userData?.firstName} ${userData?.middleName ? `${userData.middleName}` : ''}`
        }
    });

    const ownerFilterTemplate = (options: any) => {
        let usersData: any
        if (!Array.isArray(usersDataWithFullName)) {
            console.error('usersDataWithFullName is not an array');
        } else {
            usersData = [
                {value: "", userName: ""},
                ...usersDataWithFullName
            ];
        }
        return (
            <>
                <div className="mb-3 text-bold">Оберіть:</div>
                <MultiSelect
                    value={options.value}
                    optionValue="userName"
                    selectedItemsLabel={options.value ? `Обрано: ${options.value.length}` : ""}
                    filter
                    options={usersData}
                    maxSelectedLabels={0}
                    optionLabel="userName"
                    onChange={(e) => options.filterCallback(e.value, options.index)}
                    placeholder="нічого не обрано"
                    className="p-column-filter"
                />
            </>
        );
    }

    const getTotalSeconds = (date: any) => {
        if (!date) return null;
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        return hours * 3600 + minutes * 60 + seconds;
    };

    const isDefaultTime = (time: Date | null | false): boolean => {
        if (!time) return true;
        const defaultTime = getDefaultStartTime();
        return time.getTime() === defaultTime.getTime();
    };

    const durationFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return <>
            <span className="title">Виберіть час:</span>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>З*</span>
                <Calendar
                    appendTo="self"
                    id="icon"
                    placeholder="00:00:00"
                    timeOnly
                    showSeconds
                    hourFormat="24"
                    className={getActiveFilterClassName("mt-2", isTimeFilterActive('duration'))}
                    value={durationStartTime || null}
                    onChange={(e: any) => {
                        if (e.value) setdurationStartTime(e.value);
                    }}
                    onFocus={() => {
                        setdurationStartTime(null);
                    }}
                    onBlur={() => {
                        if (!durationStartTime) {
                            const defaultTime = getDefaultStartTime();
                            if (defaultTime) {
                                setdurationStartTime(defaultTime);
                            }
                        }
                    }}
                    onInput={(e: any) => {
                        const input = e.target as HTMLInputElement;

                        const digits = input.value.replace(/\D/g, "").slice(0, 6);
                        const HH = digits.slice(0, 2);
                        const MM = digits.slice(2, 4);
                        const SS = digits.slice(4, 6);

                        const display =
                            HH +
                            (digits.length > 2 ? ":" + MM : "") +
                            (digits.length > 4 ? ":" + SS : "");

                        if (input.value !== display) {
                            const caret =
                                digits.length +
                                (digits.length > 2 ? 1 : 0) +
                                (digits.length > 4 ? 1 : 0);
                            input.value = display;
                            input.setSelectionRange(caret, caret);
                        }

                        if (digits.length === 6) {
                            const h = parseInt(HH, 10);
                            const m = parseInt(MM, 10);
                            const s = parseInt(SS, 10);
                            const valid =
                                h >= 0 && h <= 23 &&
                                m >= 0 && m <= 59 &&
                                s >= 0 && s <= 59;

                            if (valid) {
                                const base =
                                    durationStartTime
                                        ? new Date(durationStartTime)
                                        : (getDefaultStartTime() ? new Date(getDefaultStartTime()) : new Date());
                                base.setHours(h, m, s, 0);
                                setdurationStartTime(base);
                            }
                        }
                    }}
                    // showOnFocus={false}
                    // hideOnDateTimeSelect={false}
                />
            </div>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>По*</span>
                <Calendar
                    appendTo="self"
                    id="icon"
                    timeOnly
                    showSeconds
                    className={getActiveFilterClassName("mt-2", isTimeFilterActive('duration'))}
                    value={durationEndTime || null}
                    onChange={(e: any) => {
                        if (e.value) setdurationEndTime(e.value);
                    }}
                    onFocus={() => {
                        setdurationEndTime(null);
                    }}
                    onBlur={() => {
                        if (!durationEndTime) {
                            const defaultTime = getDefaultStartTime();
                            if (defaultTime) {
                                setdurationEndTime(defaultTime);
                            }
                        }
                    }}
                    onInput={(e: any) => {
                        const input = e.target as HTMLInputElement;

                        const digits = input.value.replace(/\D/g, "").slice(0, 6);
                        const HH = digits.slice(0, 2);
                        const MM = digits.slice(2, 4);
                        const SS = digits.slice(4, 6);

                        const display =
                            HH +
                            (digits.length > 2 ? ":" + MM : "") +
                            (digits.length > 4 ? ":" + SS : "");

                        if (input.value !== display) {
                            const caret =
                                digits.length +
                                (digits.length > 2 ? 1 : 0) +
                                (digits.length > 4 ? 1 : 0);
                            input.value = display;
                            input.setSelectionRange(caret, caret);
                        }

                        if (digits.length === 6) {
                            const h = parseInt(HH, 10);
                            const m = parseInt(MM, 10);
                            const s = parseInt(SS, 10);
                            const valid =
                                h >= 0 && h <= 23 &&
                                m >= 0 && m <= 59 &&
                                s >= 0 && s <= 59;

                            if (valid) {
                                const base =
                                    durationEndTime
                                        ? new Date(durationEndTime)
                                        : (getDefaultStartTime() ? new Date(getDefaultStartTime()) : new Date());
                                base.setHours(h, m, s, 0);
                                setdurationEndTime(base);
                            }
                        }
                    }}
                    placeholder="00:00:00"
                    hourFormat="24"
                    // showOnFocus={false}
                    // hideOnDateTimeSelect={false}
                />
            </div>
        </>
    };

    const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return <>
            <span className="title">Виберіть дату</span>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>З*</span>
                <Calendar className="mt-2" value={isStartDateFocused ? null : (filterDateTime || getDefaultStartDate())} onChange={(e: any) => {
                    options.filterCallback(e.value, options.index);
                    setFilterDateTime(e.value)
                }} onInput={(e: any) => {
                    const input = e.target as HTMLInputElement;

                    const digits = input.value.replace(/\D/g, "").slice(0, 8); // ddMMyyyy

                    const dd = digits.slice(0, 2);
                    const mm = digits.slice(2, 4);
                    const yyyy = digits.slice(4, 8);

                    const display =
                        (dd || "") +
                        (digits.length > 2 ? "/" + mm : "") +
                        (digits.length > 4 ? "/" + yyyy : "");

                    const newCaret =
                        digits.length +
                        (digits.length > 2 ? 1 : 0) +
                        (digits.length > 4 ? 1 : 0);

                    if (input.value !== display) {
                        input.value = display;
                        input.setSelectionRange(newCaret, newCaret);
                    }

                    if (digits.length === 8) {
                        const day = parseInt(dd, 10);
                        const month = parseInt(mm, 10) - 1; // 0-based
                        const year = parseInt(yyyy, 10);
                        const parsed = new Date(year, month, day);

                        const valid =
                            !isNaN(parsed.getTime()) &&
                            parsed.getDate() === day &&
                            parsed.getMonth() === month &&
                            parsed.getFullYear() === year;

                        setFilterDateTime(valid ? parsed : null);
                    }
                }} onFocus={(e: any) => {
                    setIsStartDateFocused(true);
                }} onBlur={(e: any) => {
                    setIsStartDateFocused(false);
                    const digits = e.target.value.replace(/\D/g, "");
                    if (digits.length !== 8) {
                        e.target.value = "";
                        setFilterDateTime(getDefaultStartDate());
                    } else if (!filterDateTime) {
                        setFilterDateTime(getDefaultStartDate());
                    }
                }} minDate={new Date(2023, 5, 1)} // 01.06.2023
                          maxDate={new Date()}
                          dateFormat="dd/mm/yy" placeholder="дд/мм/рр"/>
            </div>
            <div className="flex field align-items-center max-w-15rem">
                <span className="title" style={{width: '35px'}}>По*</span>
                <Calendar className="mt-2" value={isEndDateFocused ? null : (filterEndDateTime || getDefaultEndDate())} onChange={(e: any) => {
                    setFilterEndDateTime(e.value)
                }} onInput={(e: any) => {
                    const input = e.target as HTMLInputElement;

                    const digits = input.value.replace(/\D/g, "").slice(0, 8); // ddMMyyyy

                    const dd = digits.slice(0, 2);
                    const mm = digits.slice(2, 4);
                    const yyyy = digits.slice(4, 8);

                    const display =
                        (dd || "") +
                        (digits.length > 2 ? "/" + mm : "") +
                        (digits.length > 4 ? "/" + yyyy : "");

                    const newCaret =
                        digits.length +
                        (digits.length > 2 ? 1 : 0) +
                        (digits.length > 4 ? 1 : 0);

                    if (input.value !== display) {
                        input.value = display;
                        input.setSelectionRange(newCaret, newCaret);
                    }

                    if (digits.length === 8) {
                        const day = parseInt(dd, 10);
                        const month = parseInt(mm, 10) - 1; // 0-based
                        const year = parseInt(yyyy, 10);
                        const parsed = new Date(year, month, day);

                        const valid =
                            !isNaN(parsed.getTime()) &&
                            parsed.getDate() === day &&
                            parsed.getMonth() === month &&
                            parsed.getFullYear() === year;

                        setFilterEndDateTime(valid ? parsed : null);
                    }
                }} onFocus={(e: any) => {
                    setIsEndDateFocused(true);
                }} onBlur={(e: any) => {
                    setIsEndDateFocused(false);
                    const digits = e.target.value.replace(/\D/g, "");
                    if (digits.length !== 8) {
                        e.target.value = "";
                        setFilterEndDateTime(getDefaultEndDate());
                    } else if (!filterEndDateTime) {
                        setFilterEndDateTime(getDefaultEndDate());
                    }
                }} minDate={new Date(2023, 5, 1)} // 01.06.2023
                          maxDate={new Date()}
                          dateFormat="dd/mm/yy" placeholder="дд/мм/рр"/>
            </div>
            <div className="flex max-w-15rem">
                <div className="md:col-6">
                    <div className="time-item">
                        <span className="title">З*</span>
                        <Calendar
                            appendTo="self"
                            id="icon"
                            placeholder="00:00"
                            value={isStartFinishTimeFocused ? null : (startTime || getDefaultStartTime())}
                            timeOnly
                            hourFormat="24"
                            onChange={(e) => setStartTime(e.value)}
                            onFocus={() => {
                                setIsStartFinishTimeFocused(true);
                                if (startTime) {
                                    setStartTime(null);
                                }
                            }}
                            onInput={(e: any) => {
                                const input = e.target as HTMLInputElement;

                                let digits = input.value.replace(/\D/g, "").slice(0, 4);
                                const HH = digits.slice(0, 2);
                                const MM = digits.slice(2, 4);

                                const display = HH + (digits.length > 2 ? ":" + MM : "");

                                if (input.value !== display) {
                                    const caret = digits.length + (digits.length > 2 ? 1 : 0);
                                    input.value = display;
                                    input.setSelectionRange(caret, caret);
                                }

                                if (digits.length === 4) {
                                    const h = parseInt(HH, 10);
                                    const m = parseInt(MM, 10);
                                    const valid = h >= 0 && h <= 23 && m >= 0 && m <= 59;
                                    if (valid) {
                                        const base = startTime ? new Date(startTime) : new Date();
                                        base.setHours(h, m, 0, 0);
                                        setStartTime(base);
                                    }
                                }
                            }}
                            onBlur={() => {
                                setIsStartFinishTimeFocused(false);
                            }}
                        />
                    </div>
                </div>
                <div className="md:col-6">
                    <div className="time-item">
                        <span className="title">По*</span>
                        <Calendar
                            appendTo="self"
                            id="icon"
                            placeholder="00:00"
                            value={isEndFinishTimeFocused ? null : (endTime || getDefaultEndTime())}
                            timeOnly
                            hourFormat="24"
                            onChange={(e) => setEndTime(e.value)}
                            onFocus={() => {
                                setIsEndFinishTimeFocused(true);
                                if (endTime) {
                                    setEndTime(null);
                                }
                            }}
                            onInput={(e: any) => {
                                const input = e.target as HTMLInputElement;

                                let digits = input.value.replace(/\D/g, "").slice(0, 4);
                                const HH = digits.slice(0, 2);
                                const MM = digits.slice(2, 4);

                                const display = HH + (digits.length > 2 ? ":" + MM : "");

                                if (input.value !== display) {
                                    const caret = digits.length + (digits.length > 2 ? 1 : 0);
                                    input.value = display;
                                    input.setSelectionRange(caret, caret);
                                }

                                if (digits.length === 4) {
                                    const h = parseInt(HH, 10);
                                    const m = parseInt(MM, 10);
                                    const valid = h >= 0 && h <= 23 && m >= 0 && m <= 59;
                                    if (valid) {
                                        const base = endTime ? new Date(endTime) : new Date();
                                        base.setHours(h, m, 0, 0);
                                        setEndTime(base);
                                    }
                                }
                            }}
                            onBlur={() => {
                                setIsEndFinishTimeFocused(false);
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    };

    return (
        <div className="layout-dashboard">
            <Toast ref={toast}/>
            <div className="">
                <div className="flex col-12 md:col-12 flex-wrap p-0">
                    <div className={classNames('card widget-table col-12 transition-duration-300 monitoring-table align-self-start m-0 mr-0', {'': selectedRowData && !isMobile(), 'md:col-12': !selectedRowData})}>
                        <CampaignLeadsListNav
                            handleReset={handleReset}
                            resetSorting={handleReset}
                            filterDateTime={filterDateTime}
                            setFilterDateTime={setFilterDateTime}
                            filterEndDateTime={filterEndDateTime}
                            setFilterEndDateTime={setFilterEndDateTime}
                            startTime={startTime}
                            setStartTime={setStartTime}
                            endTime={endTime}
                            setEndTime={setEndTime}
                            allTimeChecked={allTimeChecked}
                            setAllTimeChecked={setAllTimeChecked}
                            hasStartFilter={hasStartFilter}
                            setHasStartFilter={setHasStartFilter}
                            selectedCampaign={selectedCampaign}
                            setSelectedCampaign={setSelectedCampaign}
                            sortData={sortData}
                            campaignContacts={campaignContacts}
                            setValue={setValue}
                            value={value}
                        />
                        <div className="leads">
                            <div className="grid p-fluid">
                                {value === 'Список лідів' ? <div className="field col-12 md:col-12 pb-0">
                                    <DataTable value={dataWithKeys}
                                               ref={dataTableRef}
                                               filters={clearLeadsFilters}
                                               sortMode="single"
                                               onSort={customSort}
                                               scrollable
                                               scrollHeight="70vh"
                                               onFilter={customFilter}
                                               loading={contactsLoader}
                                               expandedRows={expandedRows}
                                               onRowToggle={(e) => setExpandedRows(e.data)}
                                               rowExpansionTemplate={rowExpansionTemplate}
                                               dataKey="__rowKey"
                                               emptyMessage="Нічого не знайдено"
                                               className={classNames('campaignContacts campaignLeads', {'active-header-column': orderBy === 'ContactName', 'orderByPhoneType': orderBy === 'PhoneTypeId'})}
                                    >
                                        <Column headerStyle={{width: '3rem'}} expander style={{width: '3rem'}} bodyStyle={{maxWidth: '35px'}}/>
                                        <Column header={header('Назва ліда', 'ContactName')}  body={headerTemplate}
                                                filter filterField="contactName" field="main.contactName" sortField="contactName"
                                                showFilterMatchModes={false} showFilterMenuOptions={false}
                                                filterClear={filterClearTemplate} filterApply={filterApplyTemplate} filterPlaceholder="Введіть назву лїда"
                                                headerStyle={{minWidth: '280px', width: '100%'}} style={{cursor: 'pointer', minWidth: '280px', width: '100%'}} sortable
                                                className={classNames('', {'active-header-column': orderBy === 'ContactName'})}/>
                                        <Column header={header('Власник ліда', 'OwnerUserName')} body={ownerUserNameTemplate}
                                                filter={hasUsersAccess} filterField="ownerUserName" field="main.ownerUserName" sortField="ownerUserName"
                                                showFilterMatchModes={false} showFilterMenuOptions={false}
                                                filterClear={hasUsersAccess ? filterClearTemplate : undefined} filterApply={hasUsersAccess ? filterApplyTemplate : undefined} filterElement={hasUsersAccess ? ownerFilterTemplate : undefined}
                                                filterPlaceholder="Введіть назву" headerStyle={{minWidth: '200px', width: '100%'}} style={{cursor: 'pointer', minWidth: '200px', width: '100%'}} sortable
                                                className={classNames('', {'active-header-column': orderBy === 'OwnerUserName'})}/>
                                        <Column header={header('Стан', 'IsActive')} body={isActiveTemplate}
                                                filter filterField="isActive" field="main.isActive" sortField="isActive"
                                                showFilterMatchModes={false} showFilterMenuOptions={false}
                                                filterClear={filterClearTemplate} filterApply={filterApplyTemplate} filterElement={isActiveFilterTemplate}
                                                filterPlaceholder="Введіть назву ліда"
                                                headerStyle={{minWidth: '200px', width: '100%'}} style={{cursor: 'pointer', minWidth: '200px', width: '100%'}} sortable
                                                className={classNames('', {'active-header-column': orderBy === 'IsActive'})}/>
                                        <Column header="Номер"
                                                field="phoneNo"
                                                filter showFilterMatchModes={false} showFilterMenuOptions={false}
                                                filterClear={filterClearTemplate} filterApply={filterApplyTemplate} filterElement={phoneNoFilterTemplate} filterPlaceholder="Введіть номер"
                                                headerStyle={{minWidth: '200px', width: '100%'}} style={{minWidth: '200px', width: '100%'}}/>
                                        <Column header={header('Тип номера', 'PhoneTypeId')} body={typeNumberTemplate}
                                                filter filterElement={typeFilterTemplate} showFilterMatchModes={false} showFilterMenuOptions={false}
                                                filterClear={filterClearTemplate} filterApply={filterApplyTemplate}
                                                className={classNames('', {'active-header-column': orderBy === 'PhoneTypeId'})}
                                                sortable field="phoneTypeId" headerStyle={{minWidth: '200px', width: '100%'}} style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}/>
                                        <Column header={header('Останній дзвінок', 'LastCall')} body={(e) => dateTemplate(e, 'lastCall')}
                                                filter filterElement={lastCallDateFilterTemplate} field="main.lastCall" filterField="lastCall"
                                                showFilterMatchModes={false} showFilterMenuOptions={false}
                                                filterClear={filterClearTemplate} filterApply={filterApplyTemplate} filterPlaceholder="Введіть назву лїда"
                                                sortable sortField="lastCall" headerStyle={{minWidth: '200px', width: '100%'}} style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}
                                                className={classNames('', {'active-header-column': orderBy === 'LastCall'})}/>
                                        <Column header={header('Спроби дозвону', 'CountCalled')} body={calledTemplate}
                                                headerStyle={{minWidth: '200px', width: '100%'}} style={{cursor: 'pointer', minWidth: '200px', width: '100%'}} sortable
                                                className={classNames('', {'active-header-column': orderBy === 'CountCalled'})}/>
                                        <Column header={header('Наступна спроба', 'NewCall')} body={(e) => dateTemplate(e, 'newCall')}
                                                filter filterElement={newCallDateFilterTemplate} field="main.newCall" filterField="newCall"
                                                showFilterMatchModes={false} showFilterMenuOptions={false}
                                                filterClear={filterClearTemplate} filterApply={filterApplyTemplate} filterPlaceholder="Введіть назву лїда"
                                                sortable sortField="newCall" headerStyle={{minWidth: '200px', width: '100%'}} style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}
                                                className={classNames('', {'active-header-column': orderBy === 'NewCall'})}/>
                                        <Column header={header('Результат', 'IsDone')} body={statusTemplate}
                                                filter filterElement={isDoneFilterTemplate} field="result"
                                                showFilterMatchModes={false} showFilterMenuOptions={false}
                                                filterClear={filterClearTemplate} filterApply={filterApplyTemplate} filterPlaceholder="Оберіть тип"
                                                sortable headerStyle={{minWidth: '200px', width: '100%'}} style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}
                                                className={classNames('', {'active-header-column': orderBy === 'IsDone'})}/>
                                        <Column header={header('Дата створення', 'CreateDateTime')} body={(e) => dateTemplate(e, 'createDateTime')}
                                                filterElement={dateFilterTemplate} field="main.createDateTime" filterField="createdDateTime"
                                                showFilterMatchModes={false} showFilterMenuOptions={false}
                                                filterClear={filterClearTemplate} filterApply={filterApplyTemplate} filterPlaceholder="Введіть назву лїда"
                                                sortable sortField="createdDateTime" headerStyle={{minWidth: '200px', width: '100%'}} style={{cursor: 'pointer', minWidth: '200px', width: '100%'}}
                                                className={classNames('', {'active-header-column': orderBy === 'Date'})}/>
                                        {/* {leadPhoneTypeColumns.map((column) => (
                                            <Column key={column.sortField}
                                                    header={header(column.name, column.sortField)}
                                                    body={(rowData: any) => phoneTypeCountTemplate(rowData, column)}
                                                    sortable sortField={column.sortField}
                                                    headerStyle={{minWidth: '160px', width: '100%'}}
                                                    style={{cursor: 'pointer', minWidth: '160px', width: '100%'}}
                                                    className={classNames('', {'active-header-column': orderBy === column.sortField})}/>
                                        ))} */}
                                    </DataTable>
                                    {!!campaignContacts?.items?.length && <Paginator
                                        first={first}
                                        rows={campaignContactsPageSize.size}
                                        totalRecords={totalItems}
                                        rowsPerPageOptions={[5, 10, 15, 25, 50]}
                                        onPageChange={onPageChange}
                                        className="border-top-1 border-bottom-1"
                                        template="PrevPageLink PageLinks NextPageLink RowsPerPageDropdown"
                                    />}
                                </div> :
                                    <div className="field col-12 md:col-12 pb-0">
                                        <DataTable value={dataWithKeys}
                                                   ref={dataTableRef}
                                                   filters={clearLeadsFilters}
                                                   sortMode="single"
                                                   onSort={customSort}
                                                   scrollable
                                                   scrollHeight="70vh"
                                                   onFilter={customFilter}
                                                   loading={contactsLoader}
                                                   expandedRows={expandedRows}
                                                   onRowToggle={(e) => setExpandedRows(e.data)}
                                                   rowExpansionTemplate={ownerRowExpansionTemplate}
                                                   dataKey="__rowKey"
                                                   emptyMessage="Нічого не знайдено"
                                                   className={classNames('campaignContacts campaignLeads campaignLeadsOwners', {'active-header-column': orderBy === 'ContactName', 'orderByPhoneType': orderBy === 'PhoneTypeId'})}
                                        >
                                            <Column expander style={{width: '3rem'}} headerStyle={{width: '3rem'}} bodyStyle={{maxWidth: '35px'}}/>
                                            <Column header={header('Власник ліда', 'OwnerUserName')} body={ownerUserName2Template}
                                                    filter={hasUsersAccess} filterField="ownerUserName" field="main.ownerUserName" sortField="OwnerUserName"
                                                    showFilterMatchModes={false} showFilterMenuOptions={false}
                                                    filterClear={hasUsersAccess ? filterClearTemplate : undefined} filterApply={hasUsersAccess ? filterApplyTemplate : undefined} filterElement={hasUsersAccess ? ownerFilterTemplate : undefined}
                                                    filterPlaceholder="Введіть назву" headerStyle={{minWidth: '200px', width: '100%'}} style={{cursor: 'pointer', minWidth: '200px', width: '100%'}} sortable
                                                    className={classNames('', {'active-header-column': orderBy === 'OwnerUserName'})}/>
                                            <Column header={header('Всього', 'Total')} body={allContactsTemplate}
                                                    sortable sortField="Total"
                                                    headerStyle={{minWidth: '120px', width: '100%'}} style={{cursor: 'pointer', minWidth: '120px', width: '100%'}}
                                                    className={classNames('', {'active-header-column': orderBy === 'Total'})}/>
                                            <Column header={header('Активних', 'Active')} body={activeContactsTemplate}
                                                    sortable sortField="Active"
                                                    headerStyle={{minWidth: '120px', width: '100%'}} style={{cursor: 'pointer', minWidth: '120px', width: '100%'}}
                                                    className={classNames('', {'active-header-column': orderBy === 'Active'})}/>
                                            <Column header={header('Не активні', 'Inactive')} body={inactiveContactsTemplate}
                                                    sortable sortField="Inactive"
                                                    headerStyle={{minWidth: '130px', width: '100%'}} style={{cursor: 'pointer', minWidth: '130px', width: '100%'}}
                                                    className={classNames('', {'active-header-column': orderBy === 'Inactive'})}/>
                                            <Column header={header('Спроби', 'AttemptsDone')} body={ownerAttemptsTemplate}
                                                    sortable sortField="AttemptsDone"
                                                    headerStyle={{minWidth: '140px', width: '100%'}} style={{cursor: 'pointer', minWidth: '140px', width: '100%'}}
                                                    className={classNames('', {'active-header-column': orderBy === 'AttemptsDone'})}/>
                                            <Column header={header('Успішні', 'Success')} body={successContactsTemplate}
                                                    sortable sortField="Success"
                                                    headerStyle={{minWidth: '120px', width: '100%'}} style={{cursor: 'pointer', minWidth: '120px', width: '100%'}}
                                                    className={classNames('', {'active-header-column': orderBy === 'Success'})}/>
                                            <Column header={header('Не успішні', 'Unsuccess')} body={ownerUnsuccessTemplate}
                                                    sortable sortField="Unsuccess"
                                                    headerStyle={{minWidth: '140px', width: '100%'}} style={{cursor: 'pointer', minWidth: '140px', width: '100%'}}
                                                    className={classNames('', {'active-header-column': orderBy === 'Unsuccess'})}/>
                                        </DataTable>
                                        {!!campaignContacts?.items?.length && <Paginator
                                            first={first}
                                            rows={campaignContactsPageSize.size}
                                            totalRecords={totalItems}
                                            rowsPerPageOptions={[5, 10, 15, 25, 50]}
                                            onPageChange={onPageChange}
                                            className="border-top-1 border-bottom-1"
                                            template="PrevPageLink PageLinks NextPageLink RowsPerPageDropdown"
                                        />}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                    {selectedRowData && (
                        <>
                            {windowWidth > 1248 && <div className={classNames('card widget-table col-12 md:col-12 mt-4 align-self-start sticky top-0')}>
                                <StatusesByPeriodDetails setSelectedRowData={setSelectedRowData}
                                                         setSelectedRowExpansion={setSelectedRowExpansion}
                                                         selectedRowData={selectedRowData}
                                />
                            </div>}

                            {windowWidth <= 1248 && <div>
                                <Dialog visible={visible}
                                        header={selectedRowData?.userName || 'Деталі статусу'}
                                        className="monitoring-info" onHide={() => setVisible(false)}>
                                    <StatusesByPeriodDetails setSelectedRowData={setSelectedRowData}
                                                             setSelectedRowExpansion={setSelectedRowExpansion}
                                                             selectedRowData={selectedRowData}
                                    />
                                </Dialog>
                            </div>}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
export default CampaignLeadsList;
